//
// Copyright (c) 2026 Comcast
//

import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Field } from 'react-final-form'
import styled from 'styled-components'

import SecureInputField from 'components/Shared/SecureInputField/Index'
import { customIsEqual } from './utils'

const StyledSecureInputField = styled(SecureInputField)`
  width: ${props => props.multiline ? '100%' : 'auto'};
  margin-right: ${props => props.multiline ? '0' : '80px'};
`

const FieldComponent = ({
  isReadOnly,
  value,
  onChange,
  placeholder,
  maxLength,
  multiline,
  minRowsTextArea,
  disabled,
  isError,
}) => {
  const { t } = useTranslation()

  if (isReadOnly) {
    return value
  }

  return (
    <StyledSecureInputField
      placeholder={(placeholder !== undefined) ? placeholder : t('common.typeHere')}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      multiline={multiline}
      minRows={minRowsTextArea}
      showClear={!disabled}
      disabled={disabled}
      isError={isError}
    />
  )
}

FieldComponent.propTypes = {
  isReadOnly: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  maxLength: PropTypes.number,
  multiline: PropTypes.bool,
  minRowsTextArea: PropTypes.number,
  disabled: PropTypes.bool,
  isError: PropTypes.bool,
}

const SecureField = ({
  name,
  placeholder,
  validate,
  maxLength,
  multiline,
  minRowsTextArea,
  disabled,
  isReadOnly,
}) => {
  return (
    // without custom parse empty values are not passed to onSubmit() so API doesn't get cleared strings
    // see https://github.com/final-form/react-final-form/issues/130#issuecomment-425482365
    <Field
      name={name}
      isEqual={customIsEqual}
      validate={validate}
      parse={value => (value)}
    >
      {({ input, meta }) => {
        return (
          <FieldComponent
            isReadOnly={isReadOnly}
            value={input.value}
            onChange={input.onChange}
            placeholder={placeholder}
            maxLength={maxLength}
            multiline={multiline}
            minRowsTextArea={minRowsTextArea}
            disabled={disabled}
            isError={!!meta.error?.highlight}
          />
        )
      }}
    </Field>
  )
}

SecureField.propTypes = {
  name: PropTypes.string,
  placeholder: PropTypes.string,
  validate: PropTypes.func,
  maxLength: PropTypes.number,
  multiline: PropTypes.bool,
  minRowsTextArea: PropTypes.number,
  disabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
}

export default SecureField
